/**
 * ============================================================================
 * RESET PASSWORD COMPONENT
 * ============================================================================
 * 
 * Step 2: Confirm password reset with OTP and set new password
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Input from '../common/Input';
import LanguageSwitcher from '../common/LanguageSwitcher';

/**
 * Reset Password Component
 * @returns {JSX.Element}
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmPasswordReset, loading } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language, formatPhoneNumber } = useI18n();

  const phone = location.state?.phone || '';

  const [formData, setFormData] = useState({
    otp: ['', '', '', '', '', ''],
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const inputRefs = useRef([]);

  // Auto-focus next OTP input
  useEffect(() => {
    if (formData.otp.every((digit) => digit !== '') && formData.otp.join('').length === 6) {
      // Auto-verify when all digits entered (optional)
    }
  }, [formData.otp]);

  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...formData.otp];
    newOtp[index] = value.slice(-1);
    setFormData((prev) => ({ ...prev, otp: newOtp }));

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !formData.otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setFormData((prev) => ({ ...prev, otp: newOtp.slice(0, 6) }));
      inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const code = formData.otp.join('');

    if (code.length !== 6) {
      newErrors.otp = language === 'fr' 
        ? 'Veuillez entrer le code à 6 chiffres complet' 
        : 'Please enter the complete 6-digit code';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = language === 'fr' 
        ? 'Le nouveau mot de passe est requis' 
        : 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = language === 'fr' 
        ? 'Le mot de passe doit contenir au moins 8 caractères' 
        : 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = language === 'fr' 
        ? 'Veuillez confirmer le mot de passe' 
        : 'Please confirm password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = language === 'fr' 
        ? 'Les mots de passe ne correspondent pas' 
        : 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const code = formData.otp.join('');
      // Backend expects: phone, code, newPassword, confirmPassword
      await confirmPasswordReset({
        phone,
        code,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });

      showSuccess(
        language === 'fr'
          ? 'Mot de passe réinitialisé avec succès!'
          : 'Password reset successfully!'
      );

      // Navigate to login
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (error) {
      const errorMessage = error?.message || error?.errors?.[0] || 
        (language === 'fr'
          ? 'Échec de la réinitialisation du mot de passe'
          : 'Password reset failed');
      showError(errorMessage);
    }
  };

  if (!phone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {language === 'fr' ? 'Lien invalide' : 'Invalid Link'}
          </h1>
          <Button variant="primary" onClick={() => navigate('/auth/forgot-password')}>
            {language === 'fr' ? 'Demander un nouveau code' : 'Request New Code'}
          </Button>
        </div>
      </div>
    );
  }

  const formattedPhone = formatPhoneNumber(phone);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors group"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">{language === 'fr' ? 'Retour' : 'Back'}</span>
          </button>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl mb-4 shadow-lg">
              <span className="text-3xl">🔑</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {language === 'fr' ? 'Code envoyé à' : 'Code sent to'} {formattedPhone || phone}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* OTP Input */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-4">
              {language === 'fr' ? 'Code de vérification' : 'Verification Code'}
            </label>
            <div className="flex justify-center gap-3">
              {formData.otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleOTPKeyDown(index, e)}
                  onPaste={index === 0 ? handleOTPPaste : undefined}
                  className="w-14 h-16 text-center text-3xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-4 focus:ring-blue-200 dark:focus:ring-blue-900 focus:outline-none transition-all hover:border-blue-400"
                  autoFocus={index === 0}
                />
              ))}
            </div>
            {errors.otp && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400 text-center">
                {errors.otp}
              </p>
            )}
          </div>

          {/* New Password */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleChange('newPassword', e.target.value)}
                placeholder={language === 'fr' ? 'Minimum 8 caractères' : 'Minimum 8 characters'}
                error={errors.newPassword}
                required
                className="w-full text-lg py-3 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
            </label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder={language === 'fr' ? 'Répétez le mot de passe' : 'Repeat password'}
                error={errors.confirmPassword}
                required
                className="w-full text-lg py-3 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={loading}
            className="text-lg py-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
          >
            {language === 'fr' ? 'Réinitialiser le mot de passe →' : 'Reset Password →'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
